// This must always be the first statement in the entrypoint!
import { QFood, globalAxios } from "@q-food/q-food";

if (import.meta.env.VITE_QFOOD_API_HOST === undefined || import.meta.env.VITE_QFOOD_API_HOST === "") {
	QFood.BASE_URL = `https://${window.location.host}`;
} else {
	QFood.BASE_URL = import.meta.env.VITE_QFOOD_API_HOST;
}

import { AgChartsEnterpriseModule } from "ag-charts-enterprise";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { AllEnterpriseModule, LicenseManager } from "ag-grid-enterprise";
import "./styles/main.css";

import { FontAwesomeLayersText } from "@fortawesome/vue-fontawesome";
import * as Sentry from "@sentry/vue";
import axios, { AxiosError } from "axios";
import { createApp } from "vue";
import acl from "~/plugins/Acl.ts";
import i18n from "~/plugins/I18n.ts";
import NumberFormat from "~/plugins/NumberFormat.ts";
import App from "./App.vue";
import AppDataGridBooleanCellRenderer from "./components/AppDataGridBooleanCellRenderer.vue";
import AppDataGridFilterPresetsToolPanel from "./components/AppDataGridFilterPresetsToolPanel.vue";
import AppDataGridStatusbar from "./components/AppDataGridStatusbar.vue";
import { FloatingVue } from "./plugins/FloatingVue";
import { FontAwesomeIcon, FontAwesomeLayers } from "./plugins/FontAwesome";
import VueRouter from "./plugins/VueRouter";
import { formKit, formKitConfig } from "./plugins/formKit/FormKit";

const { error } = useToast();
let currentEtag: string | null = null;

ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule.with(AgChartsEnterpriseModule)]);
LicenseManager.setLicenseKey(
	"Using_this_{AG_Grid}_Enterprise_key_{AG-088406}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Quotec}_is_granted_a_{Multiple_Applications}_Developer_License_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{19_May_2026}____[v3]_[01]_MTc3OTE0NTIwMDAwMA==cb7c8465790fc7d3c54e38a4c4206dc7",
);

if (process.env.NODE_ENV != "development") {
	setInterval(() => {
		axios
			.get("")
			.then((response) => {
				if (response.headers.etag != currentEtag && currentEtag !== null) {
					const updateAvailableDiv = document.getElementById("updateAvailable");
					updateAvailableDiv?.removeAttribute("hidden");
				}
				currentEtag = response.headers.etag;
			})
			.catch((error) => {
				console.log(error);
			});
	}, 60 * 1000);
}

globalAxios.defaults.baseURL = QFood.BASE_URL;

globalAxios.interceptors.request.use(
	(req) => {
		const qfoodSessionToken = localStorage.getItem("qfood-session-token");
		if (qfoodSessionToken != null) {
			req.headers.set("X-Auth-Token", qfoodSessionToken);
		}
		return req;
	},
	(error) => error,
);

import { startWebSocket } from "~utils/BridgeIntegration.ts";
startWebSocket();

globalAxios.interceptors.response.use(
	(res) => {
		return res;
	},
	async (responseError: AxiosError) => {
		let responseData = responseError.response?.data as Record<string, unknown> | string | undefined;

		if (responseData instanceof Blob) {
			responseData = JSON.parse(await responseData.text());
		}

		if (
			responseError.response?.status === 403 &&
			responseError.request?.responseURL.endsWith("/api/myself/session") === false
		) {
			error("U heeft niet genoeg permissies");
			console.log("Invalid permissions", responseError.request.responseURL);
			return Promise.reject("Invalid permissions");
		}

		if (responseData !== undefined && typeof responseData !== "string" && "localizedMessage" in responseData) {
			return Promise.reject(
				Object.assign({}, responseData, { status: responseError.status, message: responseData.localizedMessage }),
			);
		}

		return Promise.reject(responseError);
	},
);

const vueApp = createApp(App)
	.use(i18n)
	.use(acl)
	.use(NumberFormat)
	.use(createPinia())
	.use(formKit, formKitConfig)
	.use(FloatingVue)
	.use(VueRouter)
	.component("AppIcon", FontAwesomeIcon)
	.component("AppIconLayers", FontAwesomeLayers)
	.component("AppIconLayersText", FontAwesomeLayersText)
	/**
	 * By not importing these, HMR won't work on these components within AG-Grid.
	 */
	.component("AppDataGridFilterPresetsToolPanel", AppDataGridFilterPresetsToolPanel)
	.component("AppDataGridBooleanCellRenderer", AppDataGridBooleanCellRenderer)
	.component("AppDataGridStatusbar", AppDataGridStatusbar);

if (import.meta.env.VITE_SENTRY_DSN !== "" && import.meta.env.VITE_SENTRY_DSN !== undefined) {
	Sentry.init({
		app: vueApp,
		dsn: import.meta.env.VITE_SENTRY_DSN,
		integrations: [
			Sentry.replayIntegration({
				maskAllText: false,
				maskAllInputs: false,
				blockAllMedia: false,
				mask: [],
				unmask: ["label", "button", "h1", "h2", "h3", "h4", "h5", '[role="alert"]'],
				maskFn: (e) => e,
			}),
		],
		sendDefaultPii: true,
		// Session Replay
		replaysSessionSampleRate: 0.05, // This sets the sample rate at 5%.
		replaysOnErrorSampleRate: 1.0, // Change the sample rate to 100% when sampling sessions where errors occur.
	});
}

vueApp.mount("#app").$nextTick(() => {
	if ("electron" in window) {
		window.electron.removeLoading();
	}
});
